import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { ExpertDailyNormsListItem } from '@/types/settings.types';
import { SectionIndices } from '@/common/i-doc/i-doc.registry';

interface ExpertNormsTableProps {
  clientId: number;
  /** Джерело - запит продакта або адміна (той самий список). */
  load: (clientId: number) => Promise<ExpertDailyNormsListItem[]>;
}

// День клієнта (yyyy-MM-dd, T3): parseISO - локальна північ того ж дня, без перерахунку поясу.
const formatDay = (day: string) => format(parseISO(day), 'dd.MM.yyyy');

const COLUMNS = [
  { key: 'waterMl', labelKey: 'norm.water', unitKey: 'unit.ml' },
  { key: 'caloriesIntake', labelKey: 'norm.calories', unitKey: 'unit.kcal' },
  { key: 'caloriesBurned', labelKey: 'expertNorms.burned', unitKey: 'unit.kcal' },
  { key: 'proteinG', labelKey: 'norm.protein', unitKey: 'unit.g' },
  { key: 'fatG', labelKey: 'norm.fat', unitKey: 'unit.g' },
  { key: 'carbsG', labelKey: 'norm.carbs', unitKey: 'unit.g' },
] as const;

/** Повна історія версій власних норм експертів для клієнта (продакт / адмін); «—» - діяла стандартна норма. */
export const ExpertNormsTable: React.FC<ExpertNormsTableProps> = ({ clientId, load }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.dailyNorms' });
  const [rows, setRows] = useState<ExpertDailyNormsListItem[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(false);
    load(clientId)
      .then((data) => { if (!cancelled) setRows(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [clientId, load]);

  return (
    <Box data-i-doc={SectionIndices.CLIENT_EXPERT_NORMS}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{t('expertNorms.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('expertNorms.hint')}</Typography>
      {error ? (
        <Alert severity="warning">{t('expertNorms.loadError')}</Alert>
      ) : !rows ? (
        <CircularProgress size={24} />
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{t('expertNorms.empty')}</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('expertNorms.expert')}</TableCell>
                <TableCell>{t('expertNorms.effectiveFrom')}</TableCell>
                <TableCell>{t('expertNorms.effectiveTo')}</TableCell>
                {COLUMNS.map((c) => (
                  <TableCell key={c.key} align="right">{t(c.labelKey)}, {t(c.unitKey)}</TableCell>
                ))}
                <TableCell align="right">{t('expertNorms.updatedAt')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.expertId}-${row.effectiveFrom}`}>
                  <TableCell>{row.expertName}</TableCell>
                  <TableCell>{formatDay(row.effectiveFrom)}</TableCell>
                  <TableCell>{row.effectiveTo ? formatDay(row.effectiveTo) : t('expertNorms.current')}</TableCell>
                  {COLUMNS.map((c) => (
                    <TableCell key={c.key} align="right">{row[c.key] ?? '—'}</TableCell>
                  ))}
                  <TableCell align="right">{row.updatedAt ? format(new Date(row.updatedAt), 'dd.MM.yyyy HH:mm') : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
